import React, { FC, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useCompanyDetails } from "../../../stores/company/company.hooks";
import { fetchCompanyDetails, fetchPdsEligibleStatus } from "../../../stores/async/async-actions";
import { useStyles } from "./styles";
import moment from "moment";
import CONSTANTS from "../../../utils/constants";
import { Box, Button, Tooltip, Typography } from "@material-ui/core";
import { EditCompanyModal } from "../edit-company";
import EditIcon from "@mui/icons-material/Edit";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import FiberManualRecordIcon from "@material-ui/icons/FiberManualRecord";
import { Parser } from "html-to-react";
import { getCompany } from "../../../services/codat-service";
import {isInActiveCompany, formatConnectionSource, isNotEmpty, isEmpty, formatDate} from "../../../utils/common-utils";
import { InfoOutlined, ErrorOutline } from "@material-ui/icons";
import CustomChip from "../../../shared/layout/chip";
import {setPdsEligibleStatus} from "../../../stores/company/company-action";
import { Alert } from "@material-ui/lab";

export const CompanyDetails: FC<any> = () => {
  const [isCompanyDialogOpen, setCompanyDialogOpen] = useState(false); 
  const { id }: any = useParams();
  const classes = useStyles();
  const dispatch = useDispatch();
  const { companyDetails,pdsEligibilityDetails,isPdsDetailsUnavailable }: any = useCompanyDetails();
  const {PDSEligible} = pdsEligibilityDetails;
  const readOnlyMode = isInActiveCompany(companyDetails);
  


  const codatCreatedDate = useMemo(() => formatDate(companyDetails?.created_on), [companyDetails])
  const fyEndDate = useMemo(() => formatDate(companyDetails.fiscal_year_end_dt, CONSTANTS.MMM_YYYY_FORMAT), [companyDetails]);

  const handleOpenCompany = () => {
    setCompanyDialogOpen(true);
  };

  const printCRMEntityId = () => {
    const { crm_entity_id = null } = companyDetails;
    if (/^(\d{1,10}|None)$/.test(crm_entity_id)) return crm_entity_id;
    return "-";
  };

  const connectionStatusDotVatiant = useMemo(() => {
    if (readOnlyMode) return classes.grayDot;
    else if (companyDetails?.connection_status === "Linked") return classes.greenDot;
    return classes.redDot;
  }, [readOnlyMode, companyDetails]);

  // Get status-based styling
  const statusStyles = useMemo(() => {
    const status = companyDetails?.status?.toLowerCase();

    switch (status) {
      case "active":
        return {
          chipClass: classes.activeChip,
          iconClass: classes.activeIcon,
        };
      case "pending_renewal":
        return {
          chipClass: classes.pendingRenewalChip,
          iconClass: classes.pendingRenewalIcon,
        };
      default:
        return {
          chipClass: classes.inactiveChip,
          iconClass: classes.inactiveIcon,
        };
    }
  }, [companyDetails?.status]);

  const formattedStatus = useMemo(() => companyDetails?.status?.replaceAll("_", " "), [companyDetails?.status]);
  const formattedClientTags = useMemo(() => companyDetails?.tags ? companyDetails?.tags?.replaceAll(",", ", ") : "-", [companyDetails?.tags])

    useEffect(() => {
      dispatch(fetchCompanyDetails(id));
    }, []);

    useEffect(() => {
      const PDSFallBackState = {
        "PDSEligible": false,
        "RelationshipType": "None",
        "EntityId": "",
        "Reason": "",
        "FirstSpread": false
      }
      async function updatePdsEligibleStatus() {
        const payload = {
          cif  : companyDetails.cif || "",
          legal_entity_name  : companyDetails?.legal_entity_name?.trim() || "",
          crm_entity_id  : companyDetails.crm_entity_id || "",
        }
        dispatch(fetchPdsEligibleStatus(payload, PDSFallBackState));
      }
      if(isNotEmpty(companyDetails)){
          if(/^\d{9}$/.test(companyDetails?.cif) || companyDetails?.crm_entity_id) {
            updatePdsEligibleStatus();
          }else{
            dispatch(setPdsEligibleStatus(PDSFallBackState));
          }
      }

    }, [companyDetails]);

  const PdsEligibilityText = () => {
    if (isEmpty(pdsEligibilityDetails) || ['Customer', 'Guarantor'].includes(pdsEligibilityDetails?.RelationshipType) ) return null;

    return (
      <Box display={"flex"} alignItems={"center"} gridGap={"4px"}>
        <Typography className={classes.pdsTitle}>
          Prospect direct spreading{" "}
          {PDSEligible ? (
            "eligible"
          ) : (
            <Box className={classes.pdsTitle} alignItems={"center"} display={"inline-flex"}>
              Ineligible&nbsp;
              <Tooltip title={Parser().parse(CONSTANTS.TOOLTIP_TITLES.PDSINELIGIBILE)} placement="right" arrow>
                <InfoOutlined height={"20px"} width={"20px"} fontSize="small" color="action" />
              </Tooltip>
            </Box>
          )}
        </Typography>
      </Box>
    );
  };

  const PdsUnvailibilityText = () => {
    return (
        <Alert 
          severity="error" 
          className={classes.pdsDetailsUnavailable}
          icon={<Box className={classes.pdsDetailsUnavailableIconContainer}>
            <ErrorOutline className={classes.pdsDetailsUnavailableIcon} />
          </Box>}
        > 
          
          <Box className={classes.pdsDetailsUnavailableTextContainer}>
            <Typography variant="body2" className={classes.pdsDetailsUnavailableText}>
              {CONSTANTS.TOOLTIP_TITLES.PDSUNAVAILABLE}
            </Typography>
          </Box>
        </Alert>
    )
  }
  
  return (
    <>
      <div className={classes.conpanyDetailsRoot}>
        <div className={`${classes.pageHeader}`}>
          <Box className={classes.companyNameContainer}>
           {isPdsDetailsUnavailable ? <PdsUnvailibilityText/> :<PdsEligibilityText/>}

            <Box display={"flex"} alignItems={"center"} gridGap={"24px"}>
              <Typography className={classes.companyName}>{companyDetails.legal_entity_name || "-"}</Typography>
              {!!companyDetails?.status && (
                <CustomChip
                  className={statusStyles.chipClass}
                  toolTip={"See timeline below"}
                  label={formattedStatus}
                  icon={<InfoOutlined className={statusStyles.iconClass} />}
                />
              )}
            </Box>
          </Box>

          <div className={`${classes.paper}`}>
            <Button
              variant="outlined"
              size="small"
              color="primary"
              className={classes.editButton}
              onClick={handleOpenCompany}
              data-testid="edit-company"
              startIcon={<EditIcon />}
              disabled={readOnlyMode}
            >
              EDIT
            </Button>
          </div>
        </div>
        <div className={classes.detailsContainer}>
          <div className={classes.cellDesign}>
            <div className={classes.textSecondary}>Codat Connection</div>
            <div className={`${classes.textPrimary} ${classes.codatConnection}`}>
              {companyDetails.connection_status ? (
                <>
                  <FiberManualRecordIcon className={connectionStatusDotVatiant} data-testid="active-circle" />{" "}
                  {companyDetails.connection_status}{" "}
                </>
              ) : (
                "-"
              )}
            </div>
          </div>
          <div className={classes.cellDesign}>
            <div className={classes.textSecondary}>Monthly Refresh</div>
            <div className={classes.monthlyRefresh}>
              {companyDetails.monthly_refresh && companyDetails.monthly_refresh == true ? (
                <>
                  {" "}
                  <FiberManualRecordIcon className={classes.greenDot} data-testid="enabled-circle" />
                  Enabled{" "}
                </>
              ) : (
                <>
                  {" "}
                  <FiberManualRecordIcon className={classes.grayDot} data-testid="disabled-circle" /> Disabled{" "}
                </>
              )}
            </div>
          </div>
          <div className={classes.cellDesign}>
            <div className={classes.textSecondary}>Codat ID</div>
            <div className={classes.textPrimary}>{companyDetails.codat_company_id || "-"}</div>
          </div>
          <div className={classes.cellDesign}>
            <div className={classes.textSecondary}>Accounting Software Provider</div>
            <div className={classes.textPrimary}>{companyDetails?.platform_name || "-"}</div>
          </div>
          <div className={classes.cellDesign}>
            <div className={classes.textSecondary}>CIF</div>
            <div className={classes.textPrimary}>
              {companyDetails.cif && companyDetails.cif !== "0" ? companyDetails.cif : "-"}
            </div>
          </div>
          <div className={classes.cellDesign}>
            <div className={classes.textSecondary}>CRM Entity ID</div>
            <div className={classes.textPrimary}>{printCRMEntityId()}</div>
          </div>
          <div className={classes.cellDesign}>
            <div className={classes.textSecondary}>Team Code</div>
            <div className={classes.textPrimary}>{companyDetails.team_code || "-"}</div>
          </div>
          <div className={classes.cellDesign}>
            <div className={classes.textSecondary}>FY End date</div>
            <div className={classes.textPrimary}>{fyEndDate}</div>
          </div>
          <div className={classes.cellDesign}>
            <div className={classes.textSecondary}>Connection Source</div>
            <div className={classes.textPrimary}>{formatConnectionSource(companyDetails.connection_source) || "-"}</div>
          </div>
          <div className={classes.cellDesign}>
            <div className={classes.innerRow}>
              <div className={classes.textSecondary}>Monthly Reminder Email</div>
              <Tooltip title={Parser().parse(CONSTANTS.TOOLTIP_TITLES.MONTHLY_REMINDER_EMAIL)} placement="top" arrow>
                <InfoOutlinedIcon fontSize="small" color="action" />
              </Tooltip>
            </div>
            <div className={classes.textPrimary}>
              {companyDetails.is_monthly_email_reminder_enabled &&
              companyDetails.is_monthly_email_reminder_enabled == true ? (
                <>
                  {" "}
                  <FiberManualRecordIcon className={classes.greenDot} data-testid="active-circle" />
                  Enabled{" "}
                </>
              ) : (
                <>
                  {" "}
                  <FiberManualRecordIcon
                    className={readOnlyMode ? classes.grayDot : classes.redDot}
                    data-testid="inactive-circle"
                  />
                  Disabled{" "}
                </>
              )}
            </div>
          </div>
          <div className={classes.cellDesign}>
            <div className={classes.textSecondary}>Codat Created Date</div>
            <div className={classes.textPrimary}>{codatCreatedDate}</div>
          </div>
          <div className={classes.cellDesign}>
            <div className={classes.textSecondary}>Primary Client Email</div>
            <div className={classes.textPrimary}>{companyDetails.primary_client_email || "-"}</div>
          </div>
          <div className={classes.cellDesign}>
            <div className={classes.textSecondary}>Secondary Client Email</div>
            <div className={classes.textPrimary}>{companyDetails.secondary_client_email || "-"}</div>
          </div>
          <div className={classes.cellDesign}>
            <div className={classes.textSecondary}>CT Tags</div>
            <div className={classes.textPrimary}>{formattedClientTags}</div>
          </div>
        </div>
      </div>

      {isCompanyDialogOpen && (
        <EditCompanyModal
          companyDetails={companyDetails || ""}
          isCompanyDialogOpen={isCompanyDialogOpen}
          onCloseModal={() => {
            setCompanyDialogOpen(false);
          }}
        />
      )}
    </>
  );
};
